/**
 * The ES coexistence guards and the E-era deciders speak in CODES (`extensions.code`):
 * ES_MODE_MISMATCH, ES_RESEND_AS_ES, SETUP_IMMUTABLE, NOT_ACTIVATED, … (plan 04 §2.2; E02 handoff §5).
 * Every portal handler branches on the CODE, never on the translated message — the message is what
 * the therapist sees, verbatim, because the API already localised it by `Accept-Language`.
 *
 * Refine hands the editor whatever the GraphQL client threw: graphql-request's `ClientError`
 * carries `response.errors[]`; other wrappers carry `errors[]` / `graphQLErrors[]`. All three are
 * read, then the serialised error is scanned as a last resort (what the E12 editor did), so an
 * unexpected wrapper still yields the code.
 */
export type GraphQLErrorInfo = { code?: string; message?: string };

type GqlErr = { message?: string; extensions?: { code?: string } };

/**
 * Server codes whose message is the server's own failure words (a driver's text, a schema error):
 * English, with internals. The message is dropped so every caller's `code && message` / fallback line
 * shows the translated copy instead (founder Q-ru-2 B, 2026-10-24). An uncoded error drops it too.
 */
const GENERIC_SERVER_CODES = new Set([
  'DB_ERROR',
  'INTERNAL_SERVER_ERROR',
  'ERROR',
  'GRAPHQL_VALIDATION_FAILED',
  'GRAPHQL_PARSE_FAILED',
  'BAD_USER_INPUT',
  'PERSISTED_QUERY_NOT_FOUND',
]);

const showable = (e: GqlErr): string | undefined =>
  e.extensions?.code && !GENERIC_SERVER_CODES.has(e.extensions.code) ? e.message : undefined;

export function graphqlErrorInfo(error: unknown): GraphQLErrorInfo {
  const e = error as {
    response?: { errors?: GqlErr[] };
    errors?: GqlErr[];
    graphQLErrors?: GqlErr[];
  } | null;
  const first =
    e?.response?.errors?.[0] ?? e?.errors?.[0] ?? e?.graphQLErrors?.[0];
  if (first) {
    return { code: first.extensions?.code, message: showable(first) };
  }
  let serialised = '';
  try {
    serialised = JSON.stringify(error ?? '');
  } catch {
    serialised = String(error);
  }
  const m = /"code":"([A-Z][A-Z0-9_]+)"/.exec(serialised);
  return { code: m?.[1] };
}

export const hasGraphQLErrorCode = (error: unknown, code: string): boolean =>
  graphqlErrorInfo(error).code === code;
